import { useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import api from "@/lib/api";
import LayeredStack from "@/components/LayeredStack";
import { Users, Zap, Thermometer, Flame, Swords, Shield, Gauge, Trophy } from "lucide-react";

function StatBar({ icon: Icon, label, value, max, color }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <Icon className="w-3.5 h-3.5 shrink-0" style={{ color }} />
      <span className="w-14 label-cap text-[9px]">{label}</span>
      <div className="flex-1 h-1.5 rounded bg-muted overflow-hidden"><div className="h-full" style={{ width: `${Math.min(100, (value / max) * 100)}%`, background: color }} /></div>
      <span className="mono w-7 text-right">{value}</span>
    </div>
  );
}

export default function FleetStatus() {
  const { user } = useOutletContext();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/fleet/status").then((r) => setD(r.data)); }, []);
  if (!d) return null;

  return (
    <div data-testid="fleet-status-page">
      <span className="label-cap text-primary flex items-center gap-2"><Users className="w-4 h-4" /> Fleet Status // Operations Board</span>
      <h1 className="font-head font-black text-3xl sm:text-4xl mt-2 mb-1">Your Fleet</h1>
      <p className="text-muted-foreground mb-5 flex items-center gap-2"><Trophy className="w-4 h-4 text-secondary" /> {d.arena_wins} career arena victories across {d.robots.length} robot(s).</p>

      {d.robots.length === 0 ? <div className="panel p-8 text-center text-muted-foreground">No robots yet. <Link to="/app/create" className="text-primary underline">Fabricate one</Link>.</div> : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="fleet-grid">
          {d.robots.map((r) => {
            const s = r.stats;
            return (
              <div key={r.pet_id} data-testid={`fleet-robot-${r.pet_id}`} className={`panel-glow p-4 flex gap-4 ${r.active ? "ring-1 ring-primary" : ""}`}>
                <div className="rounded-lg overflow-hidden border border-border shrink-0 self-start"><LayeredStack layers={r.layers} core={r.core_color} rig={r.rig} cpu={r.cpu} size={88} state={r.battery <= 0 ? "off" : "idle"} /></div>
                <div className="flex-1 min-w-0 space-y-1.5">
                  <div className="flex items-center gap-2"><span className="font-head font-bold truncate">{r.name}</span>{r.active && <span className="label-cap text-[8px] text-primary">active</span>}</div>
                  <div className="flex items-center gap-1 text-xs mono text-accent"><Flame className="w-3.5 h-3.5" /> Power {r.power}</div>
                  <StatBar icon={Zap} label="Battery" value={r.battery} max={100} color="hsl(var(--primary))" />
                  <StatBar icon={Thermometer} label="Thermal" value={r.thermal} max={100} color="hsl(var(--secondary))" />
                  <StatBar icon={Swords} label="Attack" value={s.attack} max={100} color="#ff6b6b" />
                  <StatBar icon={Shield} label="Defense" value={s.defense} max={100} color="#4dd0ff" />
                  <StatBar icon={Gauge} label="Speed" value={s.speed} max={100} color="#57d977" />
                  <Link to={`/app/robots/lookup/${r.pet_id}`} data-testid={`fleet-view-${r.pet_id}`} className="text-xs text-primary hover:underline inline-block pt-1">View public profile →</Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
